import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { LANGUAGES } from '../constants';

const VizAppEditor = ({ data, onUpdate, activeLang, onLangChange }) => {
    // Legacy Check: `vizapp` structure is typically Key -> Array of Tips
    const [activeSubCat, setActiveSubCat] = useState('');
    // activeLang via props

    // Helper: Available keys
    const availableKeys = data ? Object.keys(data) : [];

    // Auto-select first subcategory via useEffect (not during render)
    useEffect(() => {
        if (!activeSubCat && availableKeys.length > 0) {
            setActiveSubCat(availableKeys[0]);
        }
    }, [availableKeys, activeSubCat]);

    // Init Logic if none
    const initVizApp = () => {
        // Create a default subcategory if none exists, e.g., 'default'
        onUpdate({
            'default': []
        });
        setActiveSubCat('default');
    };

    if (!data || availableKeys.length === 0) {
        return (
            <div className="pcr-empty-state">
                <p>No VizApp content found.</p>
                <button className="pcr-btn-primary" onClick={initVizApp}>
                    <FaPlus /> Initialize VizApp Content
                </button>
            </div>
        );
    }



    const currentList = activeSubCat ? (data[activeSubCat] || []) : [];

    // Generate unique ID for new tips
    const generateId = () => `tip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const addTip = () => {
        const newData = JSON.parse(JSON.stringify(data));
        if (!newData[activeSubCat]) newData[activeSubCat] = [];
        newData[activeSubCat].push({
            id: generateId(),
            text: { en: "", hi: "", ar: "" },
            image: "" // 'type' in legacy seems to be mixed, typically text/image
        });
        onUpdate(newData);
    };

    const removeTip = (itemId) => {
        const newData = JSON.parse(JSON.stringify(data));
        const itemIndex = newData[activeSubCat].findIndex(item => item.id === itemId);

        if (itemIndex === -1) return;

        // 1. Clear text
        if (newData[activeSubCat][itemIndex]?.text) {
            newData[activeSubCat][itemIndex].text[activeLang] = "";
        }

        // 2. Check all
        const currentItem = newData[activeSubCat][itemIndex];
        const allEmpty = LANGUAGES.every(l => {
            const val = currentItem?.text?.[l.code];
            return !val || val.trim() === "";
        });

        // 3. Remove row if globally empty
        if (allEmpty) {
            newData[activeSubCat] = newData[activeSubCat].filter(item => item.id !== itemId);
        }

        onUpdate(newData);
    };

    const updateTip = (itemId, field, val, lang = null) => {
        const newData = JSON.parse(JSON.stringify(data));
        const itemIndex = newData[activeSubCat].findIndex(item => item.id === itemId);

        if (itemIndex === -1) return;

        if (lang) {
            if (!newData[activeSubCat][itemIndex][field]) {
                newData[activeSubCat][itemIndex][field] = {};
            }
            newData[activeSubCat][itemIndex][field][lang] = val;
        } else {
            newData[activeSubCat][itemIndex][field] = val;
        }
        onUpdate(newData);
    };

    const renderEditor = () => {
        if (!activeSubCat) return null;

        return (
            <div>
                <div className="pcr-lang-tabs" style={{ marginTop: '1rem' }}>
                    {LANGUAGES.map(lang => (
                        <div
                            key={lang.code}
                            className={`pcr-lang-tab ${activeLang === lang.code ? 'active' : ''}`}
                            onClick={() => onLangChange(lang.code)}
                        >
                            {lang.label}
                        </div>
                    ))}
                </div>

                {currentList.map((item) => (
                    <div key={item.id || `fallback_${Math.random()}`} className="pcr-list-item-card">
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <label className="pcr-label-small">Tip Text</label>
                                <textarea
                                    className="pcr-textarea-small"
                                    value={item.text?.[activeLang] || ''}
                                    onChange={(e) => updateTip(item.id, 'text', e.target.value, activeLang)}
                                />
                            </div>
                            <div style={{ width: '150px' }}>
                                <label className="pcr-label-small">Type/Image</label>
                                <input
                                    type="text"
                                    className="pcr-input-small"
                                    value={item.image || ''}
                                    onChange={(e) => updateTip(item.id, 'image', e.target.value)}
                                />
                            </div>
                            <button
                                className="pcr-btn-danger-icon"
                                onClick={() => removeTip(item.id)}
                            >
                                <FaTrash />
                            </button>
                        </div>
                    </div>
                ))}
                <button className="pcr-btn-secondary" onClick={addTip}>
                    <FaPlus /> Add VizApp Tip
                </button>
            </div>
        );
    };

    return (
        <div>
            <h2 className="pcr-section-title">VizApp Content</h2>

            <div style={{ marginBottom: '1rem' }}>
                <label className="pcr-editor-label">Sub-Category</label>
                <div className="pcr-causes-grid">
                    {availableKeys.map(k => (
                        <button
                            key={k}
                            className={`pcr-cause-btn ${activeSubCat === k ? 'active warning' : ''}`}
                            onClick={() => setActiveSubCat(k)}
                        >
                            {k}
                        </button>
                    ))}
                    {/* Input to add new subcategory could go here */}
                </div>
            </div>

            {renderEditor()}
        </div>
    );
};

export default VizAppEditor;
